import { Stack, TextField } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import { useState } from 'react';
import { NodeGraphSpec, NodeGraphThresholdOptions, UNIT_SETTINGS_NAME } from '@/core';
import {
  OptionsEditorColumn,
  OptionsEditorGrid,
  OptionsEditorGroup,
  UnitSelector,
  UnitSelectorProps,
} from '@/plugin-editor-components';
import { OptionsEditorProps } from '@/plugin-system';
import { NodeGraphThresholdsEditor } from './NodeGraphThresholdsEditor';

type NodeGraphOptionsEditorProps = OptionsEditorProps<NodeGraphSpec>;

export function NodeGraphOptionsEditorSettings(props: NodeGraphOptionsEditorProps) {
  const { onChange, value } = props;

  const handleUnitChange: UnitSelectorProps['onChange'] = (newUnit) => {
    onChange(
      produce(value, (draft) => {
        draft.unit = newUnit;
      })
    );
  };

  const handleThresholdsChange = (thresholds: NodeGraphThresholdOptions) => {
    onChange(
      produce(value, (draft) => {
        draft.thresholds = thresholds;
      })
    );
  };

  const handleFromLabelChange = (next: string) => {
    onChange(
      produce(value, (draft) => {
        draft.from_label = next === '' ? undefined : next;
      })
    );
  };

  const handleToLabelChange = (next: string) => {
    onChange(
      produce(value, (draft) => {
        draft.to_label = next === '' ? undefined : next;
      })
    );
  };

  // Local state so typing doesn't re-run the panel's extractor on every
  // keystroke; commits on blur / Enter.
  const [localFromLabel, setLocalFromLabel] = useState(value.from_label ?? '');
  const [localToLabel, setLocalToLabel] = useState(value.to_label ?? '');

  // Drop local edits when the spec is reset externally (undo, programmatic
  // reset). Previous-value comparison rather than an effect — see "Adjusting
  // state when a prop changes" in the React docs.
  const [prevFromLabel, setPrevFromLabel] = useState(value.from_label);
  if (value.from_label !== prevFromLabel) {
    setPrevFromLabel(value.from_label);
    setLocalFromLabel(value.from_label ?? '');
  }
  const [prevToLabel, setPrevToLabel] = useState(value.to_label);
  if (value.to_label !== prevToLabel) {
    setPrevToLabel(value.to_label);
    setLocalToLabel(value.to_label ?? '');
  }

  const unit = value.unit ?? { kind: 'Decimal' };

  return (
    <OptionsEditorGrid>
      <OptionsEditorColumn>
        <OptionsEditorGroup title="Edge direction">
          {/* Default is alphabetical (which is how Prometheus serializes
           * labels). Explicit names exist as an escape hatch for label
           * pairs whose intended source-to-target order doesn't sort
           * alphabetically (e.g. `target` / `source`). */}
          <Stack spacing={1}>
            <TextField
              label="From label"
              fullWidth
              helperText="Optional. Series label whose value names the source node. Defaults to the alphabetically-first of the two grouping labels."
              value={localFromLabel}
              onChange={(event) => setLocalFromLabel(event.currentTarget.value)}
              onBlur={() => handleFromLabelChange(localFromLabel)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleFromLabelChange(localFromLabel);
              }}
            />
            <TextField
              label="To label"
              fullWidth
              helperText="Optional. Series label whose value names the target node. Defaults to the alphabetically-second of the two grouping labels."
              value={localToLabel}
              onChange={(event) => setLocalToLabel(event.currentTarget.value)}
              onBlur={() => handleToLabelChange(localToLabel)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleToLabelChange(localToLabel);
              }}
            />
          </Stack>
        </OptionsEditorGroup>
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        <OptionsEditorGroup title={UNIT_SETTINGS_NAME}>
          <UnitSelector value={unit} onChange={handleUnitChange} />
        </OptionsEditorGroup>
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        <NodeGraphThresholdsEditor thresholds={value.thresholds} onChange={handleThresholdsChange} />
      </OptionsEditorColumn>
    </OptionsEditorGrid>
  );
}
